import React, { useEffect, useState } from "react";

import { useForm } from "react-hook-form/dist/react-hook-form.ie11";
import { useHistory, useLocation, useParams } from "react-router-dom";

import { fireOnce, objectKeys } from "@ds-developer/frontend";
import { useAuth, useTealium } from "@hubcapp/context";
import CryptoJS from "crypto-js";
import queryString from "query-string";

import { SignInFormFields, StateProviderProps } from "./types";
import SignIn from "./SignIn";
import validationSchema from "./validationSchema";

import { Brand } from "@hubcapp/graphql";
import { LoadingScreen } from "@hubcapp/components";
import { SMS_MFA_REQUIRED } from "../../../utils/auth";
import { getBrandRoute } from "@hubcapp/utils";

const SignInStateProvider: React.FC<StateProviderProps> = ({
  initialFormFields,
}) => {
  const history = useHistory();
  const { trackEvent } = useTealium();
  const { signIn } = useAuth();
  const { brand } = useParams<{ brand: Brand }>();

  const [loading, setLoading] = useState(false);
  const [loggingInAutomatically, setLoggingInAutomatically] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [primaryErrorMessage, setPrimaryErrorMessage] = useState<string>();
  const [loginViaURL, setLoginViaURL] = useState(false);

  const { search, state: locationState } = useLocation<{ from: string }>();

  const urlParams: {
    adi?: string;
    pw?: string;
  } = queryString.parse(search);

  const {
    errors,
    formState: { isValid },
    handleSubmit,
    register,
    triggerValidation,
  } = useForm<SignInFormFields>({
    defaultValues: initialFormFields,
    mode: "onChange",
    validationSchema,
  });

  const submit = ({ adiNumber, password }: SignInFormFields) => {
    trackEvent({
      eventCategory: "Sign In",
      eventAction: "Button Click",
      eventLabel: "Sign In",
    });
    setLoading(true);
    setPrimaryErrorMessage("");

    signIn(adiNumber, password)
      .then(data => {
        if (data === SMS_MFA_REQUIRED) {
          history.push(`/${getBrandRoute(brand)}/access-code`, { adiNumber });
        } else {
          history.push(locationState?.from ?? "/home");
        }
      })
      .catch(() => {
        setLoading(false);
        setPrimaryErrorMessage(
          "Could not verify your details. Please try again.",
        );
      });
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
  };

  const handleADIEvent = () => {
    fireOnce("Sign In|Interaction|ADI Number", () => {
      trackEvent({
        eventCategory: "Sign In",
        eventAction: "Interaction",
        eventLabel: "ADI Number",
      });
    });
  };

  const handlePasswordEvent = () => {
    fireOnce("Sign In|Interaction|Password", () => {
      trackEvent({
        eventCategory: "Sign In",
        eventAction: "Interaction",
        eventLabel: "Password",
      });
    });
  };

  const handleForgottenPasswordEvent = () => {
    trackEvent({
      eventCategory: "Sign In",
      eventAction: "Link Click",
      eventLabel: "Forgotten Password",
    });
  };

  const handleCreateAccountEvent = () => {
    trackEvent({
      eventCategory: "Sign In",
      eventAction: "Link Click",
      eventLabel: "Create Account",
    });
  };

  const signUpPath = `/${getBrandRoute(brand)}/sign-up`;
  const changePasswordPath = `/${getBrandRoute(brand)}/change-password`;

  const setInitialValidation = () => {
    if (initialFormFields) {
      triggerValidation(objectKeys(initialFormFields));
    }
  };

  useEffect(() => {
    if (
      urlParams.adi &&
      urlParams.pw &&
      !loginViaURL &&
      !loggingInAutomatically
    ) {
      setLoggingInAutomatically(true);
      trackEvent({
        eventCategory: "Sign In",
        eventAction: "App sign-in",
        eventLabel: "Sign In (via app)",
      });
      setLoginViaURL(true);

      const encryptionKey = process.env.REACT_APP_ENCRYPTION_KEY;

      if (!encryptionKey) {
        throw new Error("Missing encryption key");
      }

      const decryptedAdiNumber = CryptoJS.AES.decrypt(
        urlParams.adi,
        encryptionKey,
      ).toString(CryptoJS.enc.Utf8);

      const decryptedPassword = CryptoJS.AES.decrypt(
        urlParams.pw,
        encryptionKey,
      ).toString(CryptoJS.enc.Utf8);

      signIn(decryptedAdiNumber, decryptedPassword)
        .then(data => {
          if (data === SMS_MFA_REQUIRED) {
            history.push(`/${getBrandRoute(brand)}/access-code`, {
              adiNumber: decryptedAdiNumber,
            });
          } else {
            history.push(locationState?.from ?? "/home");
          }
          setLoggingInAutomatically(false);
        })
        .catch(() => {
          history.push(
            `/${getBrandRoute(brand)}/sign-in?error=${encodeURIComponent(
              "Could not verify your details. Please try again.",
            )}`,
          );
          setLoggingInAutomatically(false);
        });
    }
  }, []);

  useEffect(setInitialValidation, []);

  if (loggingInAutomatically) {
    return <LoadingScreen />;
  }

  return (
    <SignIn
      errors={errors}
      handleClickShowPassword={handleClickShowPassword}
      handleMouseDownPassword={handleMouseDownPassword}
      handleSubmit={handleSubmit(submit)}
      isValid={isValid}
      handleADIEvent={handleADIEvent}
      handlePasswordEvent={handlePasswordEvent}
      handleForgottenPasswordEvent={handleForgottenPasswordEvent}
      handleCreateAccountEvent={handleCreateAccountEvent}
      primaryErrorMessage={primaryErrorMessage}
      loading={loading}
      register={register}
      showPassword={showPassword}
      signUpPath={signUpPath}
      changePasswordPath={changePasswordPath}
      brand={brand}
    />
  );
};

export default SignInStateProvider;
